import { Link, useNavigate } from 'react-router';
import { Button } from '@launchpad-ui/components';
import { Bar, BarChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';

import { Card, PageHeader, StatCard } from '../components/ui';
import { useCurrentUser } from '../lib/hooks/useAuth';
import { useNewHires } from '../lib/hooks/useNewHires';
import { useQaAssignments, useQaAssignmentsChart } from '../lib/hooks/useQaAssignments';
import { useDeleteReviewer, useReviewers, useUpdateReviewer } from '../lib/hooks/useReviewers';
import { useStats } from '../lib/hooks/useStats';

import styles from '../styles/common.module.css';

function formatWeekLabel(weekStr: string): string {
  const d = new Date(`${weekStr}T00:00:00Z`);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' });
}

export function Dashboard() {
  const userQuery = useCurrentUser();
  const statsQuery = useStats();
  const reviewersQuery = useReviewers();
  const assignmentsQuery = useQaAssignments();
  const hiresQuery = useNewHires();
  const chartQuery = useQaAssignmentsChart();
  const updateReviewer = useUpdateReviewer();
  const deleteReviewer = useDeleteReviewer();
  const navigate = useNavigate();

  const stats = statsQuery.data;
  const reviewers = reviewersQuery.data ?? [];
  const assignments = assignmentsQuery.data ?? [];
  const hires = hiresQuery.data ?? [];
  const chartData = (chartQuery.data ?? []).map((d) => ({ ...d, label: formatWeekLabel(d.week) }));

  const active = reviewers.filter((r) => r.active);
  const inactive = reviewers.filter((r) => !r.active);
  const activeHires = hires.filter((h) => h.active);

  const workload: Record<string, { pending: number; completed: number }> = {};
  for (const a of assignments) {
    if (!workload[a.assigned_reviewer]) workload[a.assigned_reviewer] = { pending: 0, completed: 0 };
    if (a.status === 'completed') workload[a.assigned_reviewer].completed++;
    else if (a.status === 'pending') workload[a.assigned_reviewer].pending++;
  }

  const today = new Date().toISOString().split('T')[0];
  const firstName = userQuery.data?.name?.split(' ')[0] ?? userQuery.data?.email ?? '';

  return (
    <div className={styles.page}>
      <PageHeader title={`Welcome back, ${firstName}`} subtitle="Technical Support QA Dashboard" />

      <div className={styles.statGrid}>
        <StatCard label="Active New Hires" value={stats?.activeNewHires ?? '—'} />
        <StatCard label="Pending Reviews" value={stats?.pendingQA ?? '—'} highlight />
        <StatCard label="Completed Reviews" value={stats?.completedQA ?? '—'} />
        <StatCard label="Active Reviewers" value={active.length || '—'} />
      </div>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'minmax(0, 2fr) minmax(0, 1fr)',
          gap: '1.5rem',
          marginBottom: '1.5rem',
        }}
      >
        <Card>
          <h2 className={styles.sectionTitle}>Reviewer Team</h2>
          <table className={styles.table}>
            <thead>
              <tr>
                <th className={styles.th}>Reviewer</th>
                <th className={styles.th}>Progress</th>
                <th className={styles.th} style={{ textAlign: 'center' }}>
                  Done
                </th>
                <th className={styles.th} style={{ textAlign: 'center' }}>
                  Pending
                </th>
              </tr>
            </thead>
            <tbody>
              {active.map((r) => {
                const w = workload[r.name] || { pending: 0, completed: 0 };
                const total = w.pending + w.completed;
                const pct = total ? Math.round((w.completed / total) * 100) : 0;
                return (
                  <tr key={r.id}>
                    <td className={styles.td}>
                      <Button
                        variant="minimal"
                        onPress={() => navigate(`/queue?reviewer=${encodeURIComponent(r.name)}&status=pending`)}
                      >
                        {r.name}
                      </Button>
                      {r.email && (
                        <div style={{ fontSize: '0.7rem', color: 'var(--lp-color-text-ui-secondary)' }}>{r.email}</div>
                      )}
                    </td>
                    <td className={styles.td}>
                      {total > 0 ? (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                          <div
                            style={{
                              width: '6rem',
                              height: '0.4rem',
                              borderRadius: '999px',
                              backgroundColor: 'var(--lp-color-bg-interactive-tertiary)',
                              overflow: 'hidden',
                            }}
                          >
                            <div
                              style={{
                                width: `${pct}%`,
                                height: '100%',
                                backgroundColor: 'var(--lp-color-bg-accent, #4f46e5)',
                              }}
                            />
                          </div>
                          <span
                            style={{
                              fontSize: '0.7rem',
                              color: 'var(--lp-color-text-ui-secondary)',
                            }}
                          >
                            {total}
                          </span>
                        </div>
                      ) : (
                        '—'
                      )}
                    </td>
                    <td className={styles.td} style={{ textAlign: 'center' }}>
                      {w.completed}
                    </td>
                    <td className={styles.td} style={{ textAlign: 'center' }}>
                      {w.pending}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>

          {inactive.length > 0 && (
            <div style={{ marginTop: '1rem' }}>
              <p className={styles.sectionTitle}>Inactive</p>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                {inactive.map((r) => (
                  <div
                    key={r.id}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.25rem',
                      padding: '0.25rem 0.5rem',
                      border: '1px solid var(--lp-color-border-ui-secondary)',
                      borderRadius: '999px',
                    }}
                  >
                    <span style={{ fontSize: '0.75rem', color: 'var(--lp-color-text-ui-secondary)' }}>{r.name}</span>
                    <Button
                      size="small"
                      variant="minimal"
                      onPress={() => updateReviewer.mutate({ id: r.id, patch: { active: 1 } })}
                    >
                      ↩
                    </Button>
                    <Button
                      size="small"
                      variant="minimal"
                      onPress={() => {
                        if (window.confirm('Delete this reviewer?')) {
                          deleteReviewer.mutate(r.id);
                        }
                      }}
                    >
                      ×
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </Card>

        <Card>
          <div className={styles.spaceBetween} style={{ marginBottom: '1rem' }}>
            <h2 className={styles.sectionTitle} style={{ margin: 0 }}>
              New Hires
            </h2>
            <Link to="/new-hires">Manage</Link>
          </div>
          {activeHires.length === 0 ? (
            <div className={styles.empty}>No active new hires</div>
          ) : (
            <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
              {activeHires.map((h) => {
                const daysLeft = h.review_end
                  ? Math.ceil((new Date(h.review_end).getTime() - new Date(today).getTime()) / 86400000)
                  : null;
                const pending = assignments.filter((a) => a.tse_name === h.name && a.status === 'pending').length;
                const completed = assignments.filter((a) => a.tse_name === h.name && a.status === 'completed').length;
                return (
                  <li
                    key={h.id}
                    style={{
                      padding: '0.75rem 0',
                      borderBottom: '1px solid var(--lp-color-border-ui-secondary)',
                    }}
                  >
                    <div className={styles.spaceBetween}>
                      <span style={{ fontWeight: 500 }}>{h.name}</span>
                      {pending > 0 && (
                        <span className={`${styles.badge} ${styles.badgePending}`}>{pending} pending</span>
                      )}
                    </div>
                    <div className={styles.spaceBetween} style={{ marginTop: '0.25rem', fontSize: '0.7rem' }}>
                      <span style={{ color: 'var(--lp-color-text-ui-secondary)' }}>
                        {daysLeft != null ? (daysLeft > 0 ? `${daysLeft}d left` : 'Period ended') : 'No end date'}
                      </span>
                      <span style={{ color: 'var(--lp-color-text-feedback-success, #047857)' }}>{completed} done</span>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
          <div style={{ marginTop: '0.75rem' }}>
            <Button onPress={() => navigate('/queue')}>View QA Queue</Button>
          </div>
        </Card>
      </div>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'minmax(0, 2fr) minmax(0, 1fr)',
          gap: '1.5rem',
        }}
      >
        <Card>
          <h2 className={styles.sectionTitle}>Reviews Week Over Week</h2>
          {chartData.length === 0 ? (
            <div className={styles.empty}>No data yet</div>
          ) : (
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={chartData} margin={{ top: 4, right: 8, left: -16, bottom: 4 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--lp-color-border-ui-secondary)" />
                <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                <Tooltip
                  formatter={(value, name) => [value as number, name === 'completed' ? 'Completed' : 'Pending']}
                />
                <Legend formatter={(n) => (n === 'completed' ? 'Completed' : 'Pending')} />
                <Bar dataKey="completed" stackId="a" fill="#4f46e5" />
                <Bar dataKey="pending" stackId="a" fill="#f97316" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </Card>

        <Card>
          <h2 className={styles.sectionTitle}>Quick Actions</h2>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
            <Button onPress={() => navigate('/review')}>Submit a Review</Button>
            <Button variant="minimal" onPress={() => navigate('/new-hires')}>
              Manage New Hires
            </Button>
            <Button variant="minimal" onPress={() => navigate('/reviewers')}>
              Manage Reviewers
            </Button>
          </div>
        </Card>
      </div>
    </div>
  );
}
